<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import CallDispositionForm from './CallDispositionForm.vue';
import { callStatusLabel } from '../helpers/callLabels';

// Modal wrapper pra finalização pós-chamada.
//
// Phase 2.9.10 Commit 1: modal desacoplado do CallRecord.
//   Antes: dependia de `getModalCallRecord` — se CallRecord nao chegou
//   do webhook a tempo, modal nunca abria.
//   Agora: consome `getModalContext` que carrega { callRecord, pendingCallEnd }.
//   Header, status e identificacao caem em fallback gracioso pro pendingCallEnd
//   (dados do SDK) enquanto CallRecord nao existe.

const store = useStore();
const { t } = useI18n();

const dialog = ref(null);
const form = ref(null);
const discardDialog = ref(null);
let discardContext = null;

const modalContext = computed(() => store.getters['voipDisposition/getModalContext']);
const callRecord   = computed(() => modalContext.value?.callRecord || null);
const pending      = computed(() => modalContext.value?.pendingCallEnd || null);

// Header displays — fallback: CallRecord real > pending do SDK.
const displayName = computed(() => {
  const c = callRecord.value;
  if (c?.contact?.name)  return c.contact.name;
  if (c?.peer_name)      return c.peer_name;
  if (pending.value?.peer_name) return pending.value.peer_name;
  return callRecord.value?.peer_phone || pending.value?.peer_phone || '';
});

const displayDuration = computed(() => {
  return callRecord.value?.duration_seconds ?? pending.value?.duration_seconds ?? 0;
});

const displayStatus = computed(() => {
  return callStatusLabel(t, callRecord.value?.final_status || pending.value?.final_status);
});

watch(modalContext, async context => {
  discardContext = null;
  discardDialog.value?.close();
  if (!context) { dialog.value?.close(); return; }
  await nextTick();
  if (modalContext.value === context) dialog.value?.open();
}, { immediate: true });

function requestDismiss() {
  if (form.value?.saving) return;
  if (form.value?.hasUnsavedChanges()) {
    discardContext = modalContext.value;
    discardDialog.value?.open();
    return;
  }
  store.dispatch('voipDisposition/hideModal');
}
function confirmDiscard() {
  if (discardContext && discardContext === modalContext.value && !form.value?.saving) {
    store.dispatch('voipDisposition/hideModal');
  }
  discardContext = null;
  discardDialog.value?.close();
}
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('VOIP_DISPOSITION.MODAL_TITLE')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    confirm-before-close
    @request-close="requestDismiss"
  >
    <template #description>
      <p class="text-sm text-n-slate-11 break-words">
        {{ displayName }} · {{ displayDuration }}s ·
        <span class="capitalize">{{ displayStatus }}</span>
      </p>
      <p v-if="modalContext && !callRecord" class="text-sm text-n-amber-11">
        {{ t('VOIP_DEVICES.OVERVIEW.PENDING_REVIEW_HINT') }}
      </p>
    </template>
    <CallDispositionForm
      v-if="modalContext"
      ref="form"
      :key="pending?.provider_call_id || callRecord?.id"
      :call-record="callRecord"
      :pending-call-end="pending"
      :show-dismiss="true"
      @dismissed="requestDismiss"
    />
  </Dialog>
  <Dialog
    ref="discardDialog"
    :title="t('VOIP_DEVICES.OVERVIEW.DISCARD_REVIEW_TITLE')"
    :description="t('VOIP_DEVICES.OVERVIEW.DISCARD_REVIEW_DESCRIPTION')"
    :confirm-button-label="t('VOIP_DEVICES.OVERVIEW.DISCARD_REVIEW_CONFIRM')"
    :cancel-button-label="t('VOIP_DEVICES.OVERVIEW.KEEP_REVIEW_EDITING')"
    @confirm="confirmDiscard"
    @close="discardContext = null"
  />
</template>
